// src/components/resources/templates/TemplateViewer.tsx

'use client'

import { useEffect, useMemo, type ReactNode } from 'react'
import { type Template, type TemplateDeliveryFormatId, isTemplate, TemplateDeliveryFormats } from '~/lib/resources/types/templates'
import { clientLogger } from '~/lib/logger-client'
import { GatedContentEmailCapture } from '~/components/forms/variants/gated/GatedContentEmailCapture'
import { cn } from '~/lib/utils'
import type { RelatedResource } from '~/lib/resources/types/shared/base'

interface TemplateViewerProps {
  template: Template
  activeFormat: TemplateDeliveryFormatId
  className?: string
  showEmailCapture?: boolean
  onCloseEmailCapture?: () => void
  accessGranted: boolean
  onEmailSuccess?: () => void
}

/** Live embed for formats that support it (Canva / Figma / Google). */
function EmbedPreview({ url, type }: { url: string; type: 'canva' | 'figma' | 'google' }): JSX.Element {
  const src = type === 'canva' ? `${url}?embed` : type === 'google' ? `${url}?embedded=true` : url
  return (
    <iframe
      src={src}
      title="Template preview"
      className="h-full w-full"
      allow="clipboard-write"
      loading="lazy"
      style={{ border: 0 }}
    />
  )
}

// Map template categories to icon identifiers (used to build the email model).
const TEMPLATE_CATEGORY_ICONS = {
  campaign_planning: 'campaign-planning',
  content_strategy: 'content-strategy',
  audience_research: 'audience-research',
  budget_planning: 'budget-planning',
  creative_development: 'creative-development',
  creative_strategy: 'creative-strategy',
  performance_tracking: 'performance-tracking',
} as const

/**
 * Handles the two interactive concerns of a template: an optional live embed
 * (Canva/Figma/Google) for formats that support it, and the email-capture
 * modal that gates the download. All static content (hero, instructions, tips,
 * examples, FAQ, related) is rendered by `TemplateViewerWrapper`.
 */
export function TemplateViewer({
  template,
  activeFormat,
  className,
  showEmailCapture: externalShowEmailCapture,
  onCloseEmailCapture,
  accessGranted,
  onEmailSuccess,
}: TemplateViewerProps): ReactNode {
  const showEmailCapture = externalShowEmailCapture ?? false

  const activeFormatConfig = useMemo(
    () => template.config.source.formats.find((f) => f.formatId === activeFormat),
    [template.config.source.formats, activeFormat],
  )

  const formatDisplayConfig = useMemo(() => {
    if (!activeFormat) return null
    return Object.values(TemplateDeliveryFormats).find((format) => format.id === activeFormat)
  }, [activeFormat])

  // Postmark email model for the gated download. The on-page content is owned
  // by the wrapper; this is only the delivery email's data.
  const emailTemplate = useMemo(() => {
    if (!activeFormatConfig) return null
    const emailConfig = template.config.access?.defaultGating?.requirements?.emailCapture
    if (!emailConfig?.template) return null

    const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL ?? 'https://www.adsights.ai'
    const DEFAULT_VALUES = {
      sender_name: 'The AdSights Team',
      product_name: 'AdSights',
      product_url: 'https://www.adsights.ai',
      logo_url: `https://www.adsights.ai/logo-square.png`,
      company_name: 'AdSights Inc.',
      company_address: '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702',
      support_email: 'support@adsights.ai',
    }
    const relatedResourceRefs =
      template.relatedResources?.filter((r): r is RelatedResource => typeof r !== 'string') ?? []
    const categoryIcon = TEMPLATE_CATEGORY_ICONS[template.category] ?? 'default'

    const templateModel = {
      base: { ...DEFAULT_VALUES },
      gatedResource: {
        ...DEFAULT_VALUES,
        preheader: `Access ${template.title}`,
        resource_icon_url: `${BASE_URL}/icons/resources/templates/${categoryIcon}.png`,
        resource_name: template.title,
        resource_link: `${BASE_URL}${activeFormatConfig.contentUrl}`,
        resource_preview_link: activeFormatConfig.previewUrl
          ? `${BASE_URL}${activeFormatConfig.previewUrl}`
          : `${BASE_URL}${template.config.source.defaultPreviewUrl ?? ''}`,
        resource_share_url: `${BASE_URL}/resources/templates/${template.slug}`,
        resource_description: template.description,
        ...(relatedResourceRefs[0] && {
          related_resource_1_link: `${BASE_URL}${relatedResourceRefs[0].fullRoute}`,
          related_resource_1_name: relatedResourceRefs[0].title,
        }),
        ...(relatedResourceRefs[1] && {
          related_resource_2_link: `${BASE_URL}${relatedResourceRefs[1].fullRoute}`,
          related_resource_2_name: relatedResourceRefs[1].title,
        }),
        // Full canonical route — `template.slug` alone resolves to `/<slug>`, a 404.
        twitter_share_url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(`${BASE_URL}/resources/templates/${template.slug}`)}&text=${encodeURIComponent(`Check out this ${template.title} from @AdSightsAI`)}`,
        linkedin_share_url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${BASE_URL}/resources/templates/${template.slug}`)}`,
      },
      custom: {
        template_type: 'template',
        template_id: template.id,
        template_category: template.category,
        template_format: activeFormatConfig.formatId,
        access_granted: false,
      },
    }

    return {
      templateAlias: 'gated-resource-1',
      to: '',
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      templateModel,
    }
  }, [template, activeFormatConfig])

  // Log the invalid-prop invariant from an effect (once per occurrence), never
  // from the render body — a render-path log re-POSTs to /api/log every re-render.
  useEffect(() => {
    if (!isTemplate(template)) {
      clientLogger.error('Invalid template provided to TemplateViewer', { component: 'TemplateViewer' })
    }
  }, [template])

  if (!isTemplate(template)) {
    return null
  }

  const embed = activeFormatConfig?.embedConfig
  const isGated = (activeFormatConfig?.isGated ?? false) && Boolean(emailTemplate)

  return (
    <div className={cn('w-full', className)}>
      {/* Live embed preview (only for embeddable formats, e.g. Canva) — and
          only once access is granted, so a gated embed (e.g. buyer-persona's
          Canva) stays behind the email gate rather than loading the live view
          before the visitor submits their email. */}
      {embed && (!activeFormatConfig?.isGated || accessGranted) && (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
          <div className="aspect-[16/10] w-full">
            <EmbedPreview url={embed.url} type={embed.type === 'microsoft' ? 'google' : embed.type} />
          </div>
        </div>
      )}

      {/* Gated email capture modal */}
      {isGated && emailTemplate && showEmailCapture && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCloseEmailCapture} />
          <div className="relative z-10 mx-auto w-full max-w-2xl">
            <GatedContentEmailCapture
              resourceDetails={{
                type: formatDisplayConfig?.name ?? 'Resource',
                title: template.title,
                description: template.description,
                format: formatDisplayConfig?.name,
                previewImage: activeFormatConfig?.previewUrl ?? template.config.source.defaultPreviewUrl,
              }}
              content={{
                title: { text: `Get the ${template.title}`, className: 'text-2xl font-semibold' },
                description: {
                  text: 'Enter your email and we’ll send the download link straight to your inbox.',
                  className: 'text-gray-600 dark:text-gray-400',
                },
                button: { text: 'Email me the template', loadingText: 'Sending…' },
                messages: {
                  success: 'Sent! Check your inbox for the download link.',
                  error: 'Please enter a valid email address.',
                },
              }}
              template={emailTemplate}
              validation={{
                rules: { required: true, format: true, requireCorporateEmail: false },
                messages: { required: 'Please enter your email address', corporate: 'Please use your work email' },
              }}
              analytics={{
                enabled: true,
                category: 'templates',
                action: 'email_capture',
                label: `template_${template.id}`,
                elementId: `template-email-form-${template.id}`,
              }}
              crm={{
                tags: ['template_access'],
                customFields: {
                  template_id: template.id,
                  template_name: template.title,
                  template_category: template.category,
                },
              }}
              onSuccess={onEmailSuccess}
            />
          </div>
        </div>
      )}
    </div>
  )
}
